/**
 * PAPPO — ChildFriendships
 * Ligação criança↔criança mediada pelos PAIS ("Conectar nossos filhos").
 * Renderizado na ContactsPage apenas para parent/admin. A criança nunca gera
 * link: o responsável inicia (escolhe o próprio filho + o outro responsável já
 * conectado) e o outro responsável aprova escolhendo o filho dele.
 */

import { useState, useEffect, useCallback } from 'react';
import { Users, Check, XCircle, Unlink } from 'lucide-react';
import { Button } from '../common/Button';
import {
  listChildren,
  listChildLinks,
  createChildLink,
  approveChildLink,
  rejectChildLink,
  revokeChildLink,
  type Child,
  type ChildLinks,
} from '../../services/parent';
import type { Contact } from '../../types/user';
import styles from './family.module.css';

export function ChildFriendships({ adultContacts }: { adultContacts: Contact[] }) {
  const [children, setChildren] = useState<Child[]>([]);
  const [links, setLinks] = useState<ChildLinks>({ incoming: [], outgoing: [], active: [] });
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  const [selParent, setSelParent] = useState<number | ''>('');
  const [selChild, setSelChild] = useState<number | ''>('');
  const [approveChild, setApproveChild] = useState<Record<number, number>>({});

  const load = useCallback(async () => {
    const [ch, lk] = await Promise.all([listChildren(), listChildLinks()]);
    if (ch.success && ch.data) setChildren(ch.data.children);
    if (lk.success && lk.data) setLinks(lk.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (children.length === 1) setSelChild(children[0].id);
  }, [children]);

  const send = async () => {
    if (!selParent || !selChild) {
      setFeedback('Escolha o responsável e o filho.');
      return;
    }
    setBusy(true);
    const res = await createChildLink(Number(selChild), Number(selParent));
    setBusy(false);
    setFeedback(
      res.success
        ? 'Pedido enviado! O outro responsável precisa aprovar.'
        : res.error ?? 'Não foi possível enviar.'
    );
    if (res.success) {
      setSelParent('');
      await load();
    }
  };

  const approve = async (id: number) => {
    const childId = approveChild[id] ?? (children.length === 1 ? children[0].id : 0);
    if (!childId) {
      setFeedback('Escolha qual filho seu conectar.');
      return;
    }
    const res = await approveChildLink(id, childId);
    setFeedback(
      res.success ? 'Conectados! As crianças já podem conversar.' : res.error ?? 'Erro.'
    );
    await load();
  };
  const reject = async (id: number) => {
    await rejectChildLink(id);
    await load();
  };
  const revoke = async (id: number) => {
    await revokeChildLink(id);
    setFeedback('Amizade desfeita — a conversa foi congelada.');
    await load();
  };

  const adults = adultContacts.filter(
    (c) => c.contact_user && c.contact_user.role !== 'child'
  );

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.icon}>
          <Users size={20} />
        </span>
        <div>
          <h3 className={styles.title}>Amizades dos filhos</h3>
          <p className={styles.subtitle}>
            A ligação entre as crianças é feita — e aprovada — pelos pais.
          </p>
        </div>
      </div>

      {/* Como funciona */}
      <div className={styles.steps}>
        <div className={styles.step}>
          <span className={styles.stepNum}>1</span>
          <span className={styles.stepText}>
            Conecte-se ao outro pai/mãe aqui em Contactos ("Convidar amigo").
          </span>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNum}>2</span>
          <span className={styles.stepText}>
            Escolha o seu filho e envie o pedido de amizade.
          </span>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNum}>3</span>
          <span className={styles.stepText}>
            O outro responsável escolhe o filho dele e aprova.
          </span>
        </div>
      </div>

      {children.length === 0 ? (
        <p className={styles.empty}>
          Crie um perfil de filho no Painel Parental para conectar amizades.
        </p>
      ) : (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Nova amizade</p>
          {adults.length === 0 ? (
            <p className={styles.empty}>
              Você ainda não tem outro responsável conectado. Use "Convidar amigo"
              (acima) para se conectar a um adulto primeiro.
            </p>
          ) : (
            <div className={styles.field}>
              <select
                className={`${styles.select} ${styles.grow}`}
                value={selParent}
                onChange={(e) => setSelParent(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Outro responsável…</option>
                {adults.map((c) => (
                  <option key={c.contact_user!.id} value={c.contact_user!.id}>
                    {c.contact_user!.display_name}
                  </option>
                ))}
              </select>
              <select
                className={styles.select}
                value={selChild}
                onChange={(e) => setSelChild(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Meu filho…</option>
                {children.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
              <Button size="sm" isLoading={busy} onClick={send} disabled={!selParent || !selChild}>
                Enviar pedido
              </Button>
            </div>
          )}
        </div>
      )}

      {links.incoming.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Pedidos recebidos ({links.incoming.length})</p>
          {links.incoming.map((r) => (
            <div key={r.id} className={styles.request}>
              <span className={styles.requestText}>
                <strong>{r.from_parent_name}</strong> quer conectar{' '}
                <strong>{r.from_child_name}</strong> com um filho seu.
              </span>
              <select
                className={styles.select}
                value={approveChild[r.id] ?? (children.length === 1 ? children[0].id : '')}
                onChange={(e) =>
                  setApproveChild((p) => ({ ...p, [r.id]: Number(e.target.value) }))
                }
              >
                <option value="">Meu filho…</option>
                {children.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
              <Button size="sm" leftIcon={<Check size={14} />} onClick={() => approve(r.id)}>
                Aprovar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<XCircle size={14} />}
                onClick={() => reject(r.id)}
              >
                Recusar
              </Button>
            </div>
          ))}
        </div>
      )}

      {links.active.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Amizades ativas ({links.active.length})</p>
          <div className={styles.chips}>
            {links.active.map((a) => (
              <span key={a.id} className={styles.chip}>
                {a.meu_filho} ↔ {a.amigo}
                <button
                  type="button"
                  title="Desfazer amizade"
                  className={styles.chipRemove}
                  onClick={() => revoke(a.id)}
                >
                  <Unlink size={13} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {links.outgoing.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Pedidos enviados</p>
          {links.outgoing.map((o) => (
            <div key={o.id} className={styles.request}>
              <span className={styles.requestText}>
                {o.from_child_name} → {o.to_parent_name}
              </span>
              <span className={styles.muted}>
                {o.status === 'pending' ? 'aguardando' : 'recusado'}
              </span>
            </div>
          ))}
        </div>
      )}

      {feedback && <p className={styles.feedback}>{feedback}</p>}
    </div>
  );
}
